import _ from 'lodash';
import {
  getToolIds,
  isInProgressUntilDueDate,
  toolIsManuallyGraded,
  toolRemainsInProgressUntilDueDate
} from '../utils/tool.js';
import filter from 'lodash/filter.js';

const GRADEBOOK_GRADING_TYPES = {
  GRADED: 1,
  UNGRADED: 2,
  EXTRA_CREDIT: 3
};
const GRADEBOOK_SETTINGS = {
  reportInProgressAssignments: 1,
  reportOnlyDueDatePassedAssignments: 2,
  gradingTypeStandard: 3,
  gradingTypeWeighted: 4,
  roundPercentWhole: 5,
  roundPercentTenths: 6,
  roundPercentHundredths: 7,
  showPercent: true
};

const defaults = {
  gradesToReport: GRADEBOOK_SETTINGS.reportInProgressAssignments,
  showPercent: GRADEBOOK_SETTINGS.showPercent
};

const getStudentAssignmentStatus = (readyToReport, pastDue, hasLaunched, extendedData, isAssigned, isInstructorEntered) => {
  if (isInstructorEntered) return 'Completed';
  if (readyToReport && extendedData && parseFloat(extendedData.percentComplete) < 1 && !pastDue && hasLaunched) return 'In Progress';
  if (readyToReport && extendedData && parseFloat(extendedData.percentComplete) < 1 && !pastDue && !hasLaunched) return '';
  if (readyToReport && extendedData && extendedData.isOverdue && pastDue) return 'Missed';
  if (readyToReport) return 'Completed';
  if (!isAssigned) return 'Unassigned';
  if (!readyToReport && pastDue) return 'Missed';
  if (!readyToReport && !pastDue && !hasLaunched) return '';
  if (!readyToReport && !pastDue && hasLaunched) return 'In Progress';
  return '';
};

const extractSettingAs = (settings, key, extractAs = x => x.value) => {
  const found = _.find(settings, { key });
  if (!_.isEmpty(found)) {
    return extractAs(found);
  }
  return extractAs({ value: _.get(defaults, key, '') });
};

const getSafariSafeDate = (s) => {
  if (!s) {
    return null;
  }
  if (typeof s === 'string') {
    return new Date(s.replace(' ', 'T'));
  }
  return new Date(s);
};

const applyExceptionsAndDueDate = (rawDueDate, studentAssignmentException, studentDueDateException, originalGrade, launchLogs, assignmentId, extendedData, isAssigned, tool, isInstructorEntered) => {
  // This value could be null if there's an exception,
  // in which case the exceptions due date would overwrite this
  let dueDate = getSafariSafeDate(rawDueDate);

  if (studentAssignmentException) {
    // If there's a student assignment exception, then apply it
    const exceptionDueDate = _.get(studentAssignmentException, 'exception_value.due_date');
    dueDate = getSafariSafeDate(exceptionDueDate);
  } else if (studentDueDateException) {
    // If there's a student due date exception, then apply it
    const exceptionDueDate = _.get(studentDueDateException, 'exception_value.due_date');
    dueDate = getSafariSafeDate(exceptionDueDate);
  }

  // Has the assignment due date already passed
  const now = new Date();
  const pastDue = now > dueDate;

  let studentGrade;
  if (Number.isFinite(originalGrade)) {
    // If grade exists, return that
    studentGrade = originalGrade;
  } else if (pastDue) {
    // If it's past due, return 0
    studentGrade = 0;
  } else {
    // Else grade is null
    studentGrade = null;
  }

  // If the grade has a number score, then it's considered "completed"
  let completed = Number.isFinite(originalGrade);

  // Used to determine if the student has launched the assignment
  const userAsgnLaunchLogs = launchLogs.filter(log => log.assignmentId === assignmentId || log.assignment_id === assignmentId);
  const userLaunchCount = (Array.isArray(userAsgnLaunchLogs) && userAsgnLaunchLogs.length) || 0;

  const shouldRemainInProgress = toolRemainsInProgressUntilDueDate(tool);
  if (shouldRemainInProgress) {
    completed = isInProgressUntilDueDate(pastDue, originalGrade);
  }

  const hasLaunched = !!userLaunchCount;

  // Used to display a status in the UI and drop grades
  const status = getStudentAssignmentStatus(completed, pastDue, hasLaunched, extendedData, isAssigned, isInstructorEntered);
  return { studentGrade, status, dueDate, hasLaunched };
}

const getIsAssigned = (assignment, student, exceptions = []) => {
  let isAssigned = false;
  let assignmentExceptions;

  if (Array.isArray(exceptions)) {
    assignmentExceptions = exceptions.find(ex =>
      ex.assignment_id === assignment.id &&
      ex.exception_type === 'assignment' &&
      ex.user_ids.includes(student.studentId)
    );
  }

  if ((assignment && assignment.assignment_due_at) || assignmentExceptions) {
    // If is assigned to everyone or assigned to this student, isAssigned is true
    isAssigned = true;
  }
  return isAssigned;
};

function getGradedAssignments (assignments) {
  return _.filter(assignments, a => [GRADEBOOK_GRADING_TYPES.GRADED, null].includes(a.grading_type));
}

const getCalculatedGrade = (pointsPossible, pointsEarned) => {
  if (pointsPossible === 0) {
    return 0;
  }
  return !_.isNaN(pointsEarned / pointsPossible) ? (pointsEarned / pointsPossible) : 0;
};

const isAssignmentInProgress = (assignment = {}) => {
  const now = new Date();
  let dueAt = getSafariSafeDate(_.get(assignment, 'assignment_due_at', null));

  const Exceptions = assignment.Exceptions || assignment.exceptions;

  // Iterate over exceptions and check if any due date is in the future
  if (Exceptions && Exceptions.length > 0) {
    for (const exception of Exceptions) {
      if (
        (exception.exception_type === 'due_date' || exception.exception_type === 'assignment') &&
        exception.user_ids &&
        exception.user_ids.length > 0
      ) {
        const exceptionDueDate = getSafariSafeDate(_.get(exception, 'exception_value.due_date', null));

        // If any exception's due date is in the future, mark assignment in progress
        if (now < exceptionDueDate) {
          return true;
        }
      }
    }
  }

  // If student-specific due date is available, while no exception found, then use it as the final due date
  if (assignment.studentAssignmentDueAt) {
    dueAt = getSafariSafeDate(assignment.studentAssignmentDueAt);
  }

  return now < dueAt;
};

const ngaSaysHideGrade = (assignment) => {
  const showSolutionOn = _.get(assignment, 'settings.showSolutionOn', '');
  if (showSolutionOn === 'due') {
    return isAssignmentInProgress(assignment);
  }
  return false;
};

const shouldTallyAssignment = (assignment, gradebookSettings) => {
  if (_.isEmpty(assignment)) return false;
  const gradesToReportVal = extractSettingAs(gradebookSettings, 'gradesToReport', x => _.toInteger(x.value));
  const { isDropped, status, tool, isAssigned, isInstructorEntered, studentPoints, studentGrade, subtype } = assignment;

  // assignments shouldn't be tallied if explicitly not assigned to student
  if (isAssigned === false) return false;

  // Missed manually graded assignments should be excluded from aggregations
  if (_.toLower(status) === 'missed' && toolIsManuallyGraded(tool, subtype)) {
    return false;
  }

  const reportInProgress = (
    !isAssignmentInProgress(assignment) ||
      gradesToReportVal !== GRADEBOOK_SETTINGS.reportOnlyDueDatePassedAssignments ||
      isInstructorEntered) &&
    _.isNumber(studentPoints);
  const shouldTally = reportInProgress && !ngaSaysHideGrade(assignment) && !isDropped && _.isNumber(studentGrade);
  return shouldTally;
};

/**
 * Whether a grade should be hidden until the assignment due date has passed when
 * the course uses the "report only due-date-passed assignments" setting.
 * Instructor-entered overrides remain visible.
 *
 * @param {Object} params
 * @param {number} params.gradesToReport - gradesToReport setting value
 * @param {boolean} params.pastDue - whether the assignment due date has passed
 * @param {boolean} [params.isInstructorEntered=false] - grade was entered/overridden by instructor
 * @returns {boolean}
 */
const shouldHideGradeUntilDueDate = ({
  gradesToReport,
  pastDue,
  isInstructorEntered = false
}) => {
  return (
    gradesToReport === GRADEBOOK_SETTINGS.reportOnlyDueDatePassedAssignments &&
    !pastDue &&
    !isInstructorEntered
  );
};

// calculateAssignmentsTotalsPrecision should not contain any rounding.
const calculateAssignmentsTotalsPrecision = (assignments, gradebookSettings = []) => {
  const { manual: manualToolId } = getToolIds();
  const assignmentsToTally = _.filter(assignments, a => shouldTallyAssignment(a, gradebookSettings));
  // if a grade isn't due yet we shouldn't sum it
  const pointsEarned = _.sumBy(assignmentsToTally, o => o.studentPoints);

  const gradedAssignmentsToTally = getGradedAssignments(assignmentsToTally);
  const pointsPossible = _.sumBy(gradedAssignmentsToTally, (o) => {
    const arePointsValid = _.isFinite(o.assigned_points) || _.isString(o.assigned_points);
    // In case assignment is manual, check if it's graded to add as possible points
    const shouldAddPossiblePoints = o.tool === manualToolId && !o.isInstructorEntered ? !!o.studentPoints : true;
    return arePointsValid && shouldAddPossiblePoints ? parseFloat(o.assigned_points) : 0
  });
  const grade = getCalculatedGrade(pointsPossible, pointsEarned);
  return { grade, pointsPossible, pointsEarned, assignmentsToTally };
};

// calculateAssignmentsTotals should not contain any rounding.
const calculateAssignmentsTotals = (assignments, gradebookSettings) => {
  const { pointsPossible, pointsEarned, grade } = calculateAssignmentsTotalsPrecision(assignments, gradebookSettings);
  const pointsEarnedRounded = parseFloat(pointsEarned); // Must be an instance of Number for exporting reasons.
  const pointsPossibleParsed = parseFloat(pointsPossible);
  return { pointsEarned: pointsEarnedRounded, pointsPossible: pointsPossibleParsed, grade };
};

const getDefaultCategories = (categories) => {
  let defaultGradedCategory = {};
  let defaultUngradedCategory = {};

  if (categories) {
    defaultGradedCategory = categories.find(category => category.is_default) || {};
    defaultUngradedCategory = categories.find(category => category.is_default_ungraded) || {};
  }

  return { defaultGradedCategory, defaultUngradedCategory };
}

const groupAssignmentsByCategory = (categories = [], assignments = []) => {
  const { defaultGradedCategory, defaultUngradedCategory } = getDefaultCategories(categories);
  const clonedAssignments = _.cloneDeep(assignments);
  const categoryCorrectedAssignments = clonedAssignments.map(assignment => {
    // Put assignments in bad categories into their default
    if (assignment.gradebook_category_id === 0) {
      const useDefaultUngraded = assignment.grading_type === GRADEBOOK_GRADING_TYPES.UNGRADED;
      assignment.gradebook_category_id = useDefaultUngraded ? defaultUngradedCategory.id : defaultGradedCategory.id;
    }
    return assignment;
  });
  const assignmentGroups = _.groupBy(
    categoryCorrectedAssignments,
    (assignment) => assignment.gradebook_category_id
  );
  return assignmentGroups;
}

// calculateGroupedWeightedAssignmentTotals should not contain any rounding.
const calculateGroupedWeightedAssignmentTotals = (
  assignments,
  weights,
  gradebookSettings,
  categories
) => {
  const assignmentGroups = groupAssignmentsByCategory(categories, assignments);
  const categoryTotals = _.map(assignmentGroups, (group) => {
    const firstGroup = _.head(group);
    if (firstGroup) {
      const foundWeight = weights.find(testWeight => testWeight.category_id === firstGroup.gradebook_category_id);
      const weight = foundWeight ? foundWeight.weight : 0;
      const categoryId = firstGroup.gradebook_category_id;
      return {
        ...calculateAssignmentsTotalsPrecision(group, gradebookSettings), weight, categoryId
      };
    }
    return null;
  });

  let emptyCategories = [...weights];
  categoryTotals.forEach((categoryTotal) => {
    emptyCategories = emptyCategories.filter(category => (category.category_id !== categoryTotal.categoryId));
  });

  if (emptyCategories.length > 0) {
    emptyCategories.forEach((category) => {
      category.weight = parseInt(category.weight, 10) || 0;
    });
  }

  let filteredTotals = [];
  _.forEach(categoryTotals, (category) => {
    category.weight = parseInt(category.weight, 10) || 0;
    if (category.pointsPossible !== 0) {
      const assignmentTallyToIndex = _.findIndex(category.assignmentsToTally, assignment => (assignment.studentGrade || assignment.studentGrade === 0));
      if (assignmentTallyToIndex !== -1) {
        filteredTotals.push(category);
      }
    }
  });

  let grade = 0;
  let pointsEarned = 0;
  let pointsPossible = 0;
  filteredTotals = filteredTotals.filter((category) => category.weight);

  let weightTotal = 0;
  const gradesByCategoryId = {};
  filteredTotals.forEach((categoryTotal) => {
    const nextWeight = parseInt(categoryTotal.weight, 10);
    const nextPointsEarned = parseFloat(categoryTotal.pointsEarned);
    const nextPointsPossible = parseFloat(categoryTotal.pointsPossible);
    const nextGrade = getCalculatedGrade(nextPointsPossible, nextPointsEarned) * nextWeight;
    grade += nextGrade;
    pointsEarned += nextPointsEarned;
    pointsPossible += nextPointsPossible;
    weightTotal += nextWeight;
    gradesByCategoryId[categoryTotal.categoryId] = {
      grade: nextGrade,
      pointsEarned: nextPointsEarned,
      pointsPossible: nextPointsPossible,
      weight: nextWeight
    };
  });
  if (grade !== 0) {
    grade /= weightTotal;
  }
  return { grade, pointsEarned, pointsPossible, gradesByCategoryId };
};

const isPastDue = (dueDate) => {
  if (dueDate) {
    const now = new Date();
    const dueAt = getSafariSafeDate(dueDate);
    return now >= dueAt;
  }
  return true;
}

const dropGrades = ({ categories, unfilteredStudentAssignmentsAndGrades, student, exceptions }) => {
  const myUnfilteredStudentAssignmentsAndGrades = _.cloneDeep(unfilteredStudentAssignmentsAndGrades);
  categories.forEach((category) => {
    const foundAssignments = _.filter(myUnfilteredStudentAssignmentsAndGrades, (assignment) => {
      const gradebookCategory = assignment.gradebook_category_id ? assignment.gradebook_category_id : 0;
      const isAssigned = getIsAssigned(assignment, student, exceptions);
      const isCompletedOrMissed = assignment.status === 'Completed' || (assignment.status === 'Missed' && !toolIsManuallyGraded(assignment.tool, assignment.subtype))
      return isAssigned &&
        gradebookCategory === category.id &&
        isCompletedOrMissed &&
        assignment.grading_type === GRADEBOOK_GRADING_TYPES.GRADED &&
        isPastDue(assignment.studentAssignmentDueAt);
    });

    if (category.drop_lowest_num_grades) {
      const sorted = _.orderBy(foundAssignments, ['studentGrade', 'assigned_points', 'id'], ['asc', 'desc', 'asc']);
      const quantityDroppedAssignments = foundAssignments.length <= category.drop_lowest_num_grades ? foundAssignments.length - 1 : category.drop_lowest_num_grades;
      // Set the isDropped attribute:
      _.forEach(sorted, (item, index) => (item.isDropped = (index < quantityDroppedAssignments)));
    }
  });

  return myUnfilteredStudentAssignmentsAndGrades;
};

const round = (value, decimals, ignoreDecimalZeros) => {
  const rounded = _.round(value, decimals);
  if (ignoreDecimalZeros) return rounded;
  return rounded.toFixed(decimals);
};

const getTotals = (assignments, categoryWeights, settings, categories) => {
  const gradingType = extractSettingAs(settings, 'gradingType', (x) =>
    _.toInteger(x.value)
  );

  return gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted
    ? calculateGroupedWeightedAssignmentTotals(
      assignments,
      categoryWeights,
      settings,
      categories
    )
    : calculateAssignmentsTotals(assignments, settings);
};

const getCategoryTotals = ({ category, weights, settings, categories }) => {
  const { isUngraded, weight, assignments } = category;
  const categoryTotals = getTotals(assignments, weights, settings, categories);
  return isUngraded
    ? {
        name: 'Category Total',
        numCompleted: filter(assignments, (a) => a.status === 'completed')
          .length,
        isUngraded: true,
        isAggregateCell: true
      }
    : {
        name: 'Category Total',
        studentGrade: categoryTotals.grade,
        studentPoints: categoryTotals.pointsEarned,
        assignedPoints: categoryTotals.pointsPossible,
        assigned_points: categoryTotals.pointsPossible,
        weightedCategoryTotal: round(categoryTotals.grade * weight, 2, true),
        isAggregateCell: true
      };
};

const getNonCategoryTotals = (assignments, settings, name) => {
  const nonCategoryTotal = calculateAssignmentsTotals(assignments, settings)

  return {
    name,
    studentGrade: nonCategoryTotal.grade,
    studentPoints: nonCategoryTotal.pointsEarned,
    assignedPoints: nonCategoryTotal.pointsPossible,
    assigned_points: nonCategoryTotal.pointsPossible,
    isAggregateCell: true,
    isUngraded: false
  };
}

const getUngradedCompletedStatus = (percentComplete, studentGrade) => {
  if (percentComplete && percentComplete < 1) {
    return 'missed';
  }
  if (percentComplete >= 1) {
    return 'completed';
  }
  return studentGrade > 0 ? 'completed' : 'missed';
};

const getUngradedStatus = (assignment, gradebookSettings, ignoreReportSetting = false) => {
  const {
    status,
    isDropped,
    studentGrade,
    isAssigned = true,
    percentComplete,
    assignment_due_at
  } = assignment || {
    studentGrade: 0
  };

  if (!isAssigned) {
    return 'unassigned';
  }
  if (isDropped) {
    return getUngradedCompletedStatus(percentComplete, studentGrade);
  }
  if (status && status.toLowerCase() === 'missed') {
    return status.toLowerCase();
  }

  if (_.isNumber(percentComplete) && percentComplete >= 1) {
    // Check if we should respect the "report only after due date" setting

    if (gradebookSettings && !ignoreReportSetting) {
      const gradesToReport = extractSettingAs(gradebookSettings, 'gradesToReport', (x) => _.toInteger(x.value));
      if (gradesToReport === GRADEBOOK_SETTINGS.reportOnlyDueDatePassedAssignments && assignment_due_at) {
        const now = new Date();
        const dueDate = new Date(assignment_due_at);
        const isPastDue = now > dueDate;
        if (!isPastDue) {
          // Assignment is not past due yet, so don't mark as completed
          return status === '' && percentComplete ? 'progress' : 'na';
        }
      }
    }
    return 'completed';
  }

  if (status === '' && percentComplete) {
    return 'progress';
  }
  if (_.isNumber(studentGrade)) {
    return getUngradedCompletedStatus(percentComplete, studentGrade);
  }
  return 'na';
};

const getUngradeIconText = (assignment) => {
  const status = getUngradedStatus(assignment);
  if (status === 'completed') {
    return 'Complete';
  } else if (status === 'missed' && !toolIsManuallyGraded(assignment.tool, assignment.subtype)) {
    return 'Incomplete';
  } else if (status === 'unassigned') {
    return '-';
  }
  return '--';
};

/**
 * Counts completed and missed assignments from an array of assignments.
 * Returns an object with assignmentsCompleted and assignmentsPossible (completed + missed).
 * @param {Array} assignments - Array of assignment objects with a status property
 * @returns {Object} Object with assignmentsCompleted and assignmentsPossible properties
 */
const getAssignmentCounts = (assignments) => {
  let assignmentsCompleted = 0;
  let assignmentsPossible = 0;

  assignments.forEach(assignment => {
    const status = assignment.status ? assignment.status.toLowerCase() : '';
    const completedStatus = 'completed';
    const missedStatus = 'missed';

    if (status === completedStatus) {
      assignmentsCompleted++;
      assignmentsPossible++;
    } else if (status === missedStatus) {
      assignmentsPossible++;
    }
  });

  return {
    assignmentsCompleted,
    assignmentsPossible
  };
};

const sortStudentsByName = (students, direction = 'asc') => {
  return students.sort((a, b) => {
    const aLastName = a.lastName || a.last_name;
    const bLastName = b.lastName || b.last_name;
    const aFirstName = a.firstName || a.first_name;
    const bFirstName = b.firstName || b.first_name;
    const fullNameA = aLastName.concat(', ').concat(aFirstName);
    const fullNameB = bLastName.concat(', ').concat(bFirstName);
    if (direction === 'asc') {
      return fullNameA.localeCompare(fullNameB, 'en', { sensitivity: 'base' });
    }
    return fullNameB.localeCompare(fullNameA, 'en', { sensitivity: 'base' });
  });
};

const compareWithNameTiebreaker = (diff, a, b) =>
  diff === 0
    ? (a.name || '').localeCompare(b.name || '', 'en', { numeric: true })
    : diff;

export {
  GRADEBOOK_GRADING_TYPES,
  GRADEBOOK_SETTINGS,
  getStudentAssignmentStatus,
  extractSettingAs,
  getSafariSafeDate,
  applyExceptionsAndDueDate,
  getCalculatedGrade,
  isAssignmentInProgress,
  shouldTallyAssignment,
  shouldHideGradeUntilDueDate,
  calculateAssignmentsTotals,
  calculateAssignmentsTotalsPrecision,
  calculateGroupedWeightedAssignmentTotals,
  dropGrades,
  round,
  getDefaultCategories,
  getIsAssigned,
  getTotals,
  getUngradedCompletedStatus,
  getUngradeIconText,
  getUngradedStatus,
  groupAssignmentsByCategory,
  getCategoryTotals,
  getNonCategoryTotals,
  getAssignmentCounts,
  sortStudentsByName,
  compareWithNameTiebreaker
};
